import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUpdateStaff, useDeleteStaff } from "@/hooks/use-staff";
import { useContacts } from "@/hooks/use-contacts";
import { useActivitiesByStaff } from "@/hooks/use-activities";
import { ActivitiesTab } from "./ActivitiesTab";
import { useToast } from "@/hooks/use-toast";
import { AvatarUpload } from "./AvatarUpload";
import { Users, Trash2, UserCircle, Calendar } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface StaffEditModalProps {
  staff: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const StaffEditModal = ({ staff, open, onOpenChange }: StaffEditModalProps) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role: "staff",
    permissions: "staff",
    title: "",
    phone: "",
    address: "",
    details: "",
    profile_image_url: "",
  });

  const updateStaff = useUpdateStaff();
  const deleteStaff = useDeleteStaff();
  const { data: contacts = [] } = useContacts();
  const { data: activities = [], isLoading: activitiesLoading } = useActivitiesByStaff(staff.id);
  const { toast } = useToast();

  // Filter contacts that are assigned to this staff member
  const staffContacts = contacts.filter(contact => 
    contact.internal_owner_id === staff.id || contact.external_owner_id === staff.id
  );

  useEffect(() => {
    if (staff && open) {
      setFormData({
        first_name: staff.first_name || "",
        last_name: staff.last_name || "",
        email: staff.email || "",
        role: staff.role || "staff",
        permissions: staff.permissions || staff.role || "staff",
        title: staff.title || "",
        phone: staff.phone || "",
        address: staff.address || "",
        details: staff.details || "",
        profile_image_url: staff.profile_image_url || "",
      });
    }
  }, [staff, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }
    
    try {
      await updateStaff.mutateAsync({ id: staff.id, data: formData });
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error updating staff:", error);
      
      // Handle specific database constraint errors
      if (error?.code === '23505' && error?.message?.includes('crm_staff_email_key')) {
        toast({
          title: "Email Already Exists",
          description: "Another staff member already uses this email address. Please use a different email.",
          variant: "destructive",
        });
      }
    }
  };

  const handleDelete = () => {
    deleteStaff.mutate(staff.id, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    if (!firstName && !lastName) return "ST";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (!staff) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-slate-50 border-slate-200 rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="bg-slate-800 -mx-6 -mt-6 px-6 py-4 rounded-t-xl">
          <DialogTitle className="text-slate-50 text-xl font-semibold flex items-center gap-2">
            <UserCircle className="h-5 w-5" />
            Edit Staff Member
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="details" className="w-full mt-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="contacts">
              <Users className="h-4 w-4 mr-2" />
              Assigned Contacts ({staffContacts.length})
            </TabsTrigger>
            <TabsTrigger value="activities">
              <Calendar className="h-4 w-4 mr-2" />
              Activities ({activities.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="details">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Profile Image Upload */}
              <div className="space-y-2">
                <Label className="text-slate-800 font-medium">Profile Image</Label>
                <AvatarUpload
                  currentImageUrl={formData.profile_image_url}
                  onImageChange={(imageUrl) => setFormData({ ...formData, profile_image_url: imageUrl })}
                  fallbackText={getInitials(formData.first_name, formData.last_name)}
                  size="lg"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-slate-800 font-medium">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg"
                    placeholder="Enter first name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-slate-800 font-medium">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg"
                    placeholder="Enter last name"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="email" className="text-slate-800 font-medium">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg"
                    placeholder="Enter email address"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title" className="text-slate-800 font-medium">Job Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg"
                    placeholder="Enter job title"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="permissions" className="text-slate-800 font-medium">Permissions</Label>
                  <Select 
                    value={formData.permissions} 
                    onValueChange={(value) => setFormData({ ...formData, permissions: value, role: value })}
                  >
                    <SelectTrigger className="border-slate-300 focus:border-teal-500 rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-50 border-slate-200 rounded-lg">
                      <SelectItem value="staff" className="hover:bg-teal-50">Staff</SelectItem>
                      <SelectItem value="manager" className="hover:bg-teal-50">Manager</SelectItem>
                      <SelectItem value="admin" className="hover:bg-teal-50">Admin</SelectItem>
                      <SelectItem value="superuser" className="hover:bg-teal-50">Superuser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-800 font-medium">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg"
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address" className="text-slate-800 font-medium">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg"
                    placeholder="Enter address"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="details" className="text-slate-800 font-medium">Details</Label>
                  <Textarea
                    id="details"
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg"
                    placeholder="Enter any additional details..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-between pt-6 border-t border-slate-200">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Staff Member
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Staff Member</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete {staff.first_name} {staff.last_name}? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    className="border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateStaff.isPending}
                    className="bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg shadow-md"
                  >
                    {updateStaff.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            </form>
          </TabsContent>
          
          <TabsContent value="contacts">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Contacts assigned to {staff.first_name} {staff.last_name}</h3>
              {staffContacts.length === 0 ? (
                <p className="text-slate-600">No contacts assigned to this staff member.</p>
              ) : (
                <div className="space-y-2">
                  {staffContacts.map((contact) => (
                    <div key={contact.id} className="p-3 border rounded-lg bg-white">
                      <div className="font-medium">{contact.first_name} {contact.last_name}</div>
                      <div className="text-sm text-slate-600">{contact.email}</div>
                      {contact.job_title && (
                        <div className="text-sm text-slate-600">{contact.job_title}</div>
                      )}
                      {contact.organizations && (
                        <div className="text-sm text-slate-600">
                          Company: {contact.organizations.name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="activities" className="flex-1 overflow-auto">
            <ActivitiesTab 
              activities={activities}
              isLoading={activitiesLoading}
              staffId={staff.id}
              title="Staff Activities"
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
